import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ExpandMore, 
  CloudUpload, 
  Sms, 
  Email, 
  CheckCircle,
  Delete,
  WhatsApp
} from '@mui/icons-material';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { uploadContactsFromForm } from '../services/contactService';
import { getAllTemplates } from '../services/templateService';
import { NotificationMode } from '../types/contact';
import type { SurveyTemplate } from '../types/template';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%'
}));

const StyledCard = styled(Card)({
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  boxShadow: 'none',
  marginBottom: '24px'
});

interface CSVRow {
  name: string;
  email: string;
  phone: string;
}

interface TemplateOption {
  id: string;
  name: string;
  content: string;
  subject?: string;
}

// Default fallback templates if API fails
const DEFAULT_SMS_TEMPLATES: TemplateOption[] = [
  {
    id: 'sms_default',
    name: 'Default Template',
    content: "Hi [Customer Name]! Thanks for choosing [Business Name]. We'd love your feedback: [Short Survey Link]"
  }
];

const DEFAULT_EMAIL_TEMPLATES: TemplateOption[] = [
  {
    id: 'email_default',
    name: 'Default Template',
    subject: 'We Value Your Feedback',
    content: "Dear [Customer Name],\n\nThank you for your recent experience with [Business Name]. We'd appreciate it if you could take a moment to share your feedback.\n\n[Survey Link]\n\nBest regards,\n[Business Name] Team"
  }
];

const WHATSAPP_TEMPLATES: TemplateOption[] = [
  {
    id: 'wa1',
    name: 'Quick Request',
    content: "Hi [Customer Name]! 👋\n\nThanks for your purchase! We'd love your feedback.\n\nReview: [Short Survey Link]\n\n- [Business Name]"
  }
];

const CustomerUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  const campaignId = location.state?.campaignId;

  // Fetch templates from API on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true);
        setTemplateError(null);
        
        const response = await getAllTemplates();
        
        if (response.success && response.data.templates) {
          const templates = response.data.templates;
          
          // Separate SMS and Email templates
          const sms = templates
            .filter((t: SurveyTemplate) => t.template_type === 'sms')
            .map((t: SurveyTemplate, index: number) => ({
              id: t.id || `sms_${index}`,
              name: t.template_name,
              content: t.template_content
            }));
          
          const email = templates
            .filter((t: SurveyTemplate) => t.template_type === 'email')
            .map((t: SurveyTemplate, index: number) => ({
              id: t.id || `email_${index}`,
              name: t.template_name,
              subject: t.subject || 'Survey Request',
              content: t.template_content
            }));
          
          // Update templates if we got data from API
          if (sms.length > 0) {
            setSmsTemplates(sms);
            setSelectedSmsTemplate(sms[0].id);
          } else {
            setSmsTemplates(DEFAULT_SMS_TEMPLATES);
            setSelectedSmsTemplate(DEFAULT_SMS_TEMPLATES[0].id);
          }
          
          if (email.length > 0) {
            setEmailTemplates(email);
            setSelectedEmailTemplate(email[0].id);
          } else {
            setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
            setSelectedEmailTemplate(DEFAULT_EMAIL_TEMPLATES[0].id);
          }
          
          console.log('✅ Templates loaded from API:', { sms: sms.length, email: email.length });
        } else {
          // Use defaults if API returns no templates
          setSmsTemplates(DEFAULT_SMS_TEMPLATES);
          setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
          setSelectedSmsTemplate(DEFAULT_SMS_TEMPLATES[0].id);
          setSelectedEmailTemplate(DEFAULT_EMAIL_TEMPLATES[0].id);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setTemplateError('Failed to load templates. Using default templates.');
        
        // Use default templates on error
        setSmsTemplates(DEFAULT_SMS_TEMPLATES);
        setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
        setSelectedSmsTemplate(DEFAULT_SMS_TEMPLATES[0].id);
        setSelectedEmailTemplate(DEFAULT_EMAIL_TEMPLATES[0].id);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CSVRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['email']);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState('');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');
  const [selectedWhatsappTemplate, setSelectedWhatsappTemplate] = useState('wa1');
  const [customSmsText, setCustomSmsText] = useState('');
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [customEmailText, setCustomEmailText] = useState('');
  const [customWhatsappText, setCustomWhatsappText] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>('sms');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeDialogType, setUpgradeDialogType] = useState<'template' | 'usage_limit'>('template');

  // Template state
  const [smsTemplates, setSmsTemplates] = useState<TemplateOption[]>(DEFAULT_SMS_TEMPLATES);
  const [emailTemplates, setEmailTemplates] = useState<TemplateOption[]>(DEFAULT_EMAIL_TEMPLATES);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        setError('CSV file is empty');
      return;
    }

      // Parse headers (case-insensitive)
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const nameIndex = headers.findIndex(h => h.includes('name'));
      const emailIndex = headers.findIndex(h => h.includes('email'));
      const phoneIndex = headers.findIndex(h => h.includes('phone'));

      if (nameIndex === -1 && emailIndex === -1 && phoneIndex === -1) {
        setError('CSV must contain at least one of: Name, Email, or Phone columns');
      return;
    }

      // Parse data rows (first 10 for preview)
      const dataRows = lines.slice(1);
      const preview: CSVRow[] = [];
      
      for (let i = 0; i < Math.min(10, dataRows.length); i++) {
        const values = dataRows[i].split(',').map(v => v.trim());
        preview.push({
          name: nameIndex !== -1 ? values[nameIndex] || '' : '',
          email: emailIndex !== -1 ? values[emailIndex] || '' : '',
          phone: phoneIndex !== -1 ? values[phoneIndex] || '' : ''
        });
      }

      setCsvPreview(preview);
      setTotalRows(dataRows.length);
      setError(null);
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsText(file);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit');
      return;
    }

    setCurrentFile(file);
    parseCSV(file);
  };

  const handleRemoveFile = () => {
    setCurrentFile(null);
    setCsvPreview([]);
    setTotalRows(0);
  };

  const handleChannelChange = (_event: React.MouseEvent<HTMLElement>, newChannels: string[]) => {
    if (newChannels.length > 0) {
      setSelectedChannels(newChannels);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone'],
      ['John Doe', 'john.doe@example.com', '+1234567890'],
      ['Jane Smith', 'jane.smith@example.com', '+0987654321']
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'customer_template.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getInvitationText = () => {
    // Priority: Email > WhatsApp (SMS is coming soon, so skip it)
    // Note: SMS is coming soon, so we skip it for now
    if (selectedChannels.includes('whatsapp')) {
      const template = WHATSAPP_TEMPLATES.find(t => t.id === selectedWhatsappTemplate);
      return customWhatsappText || template?.content || '';
    }
    if (selectedChannels.includes('email')) {
      const template = emailTemplates.find(t => t.id === selectedEmailTemplate);
      return customEmailText || template?.content || '';
    }
    return '';
  };

  const handleSendInvitations = async () => {
    if (!currentFile || !campaignId) {
      setError('Please upload a file and ensure campaign ID is set');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      const notificationModes: NotificationMode[] = [];
      if (selectedChannels.includes('email')) notificationModes.push(NotificationMode.EMAIL);
      // SMS is coming soon, so skip it for now
      // if (selectedChannels.includes('sms')) notificationModes.push(NotificationMode.SMS);

      const response = await uploadContactsFromForm({
        campaignId,
        file: currentFile,
        notificationModes,
        notificationText: getInvitationText()
      });

      if (response.success && response.data) {
        const uploadedCount = response.data.total_inserted || 0;
        const skippedCount = response.data.total_skipped || 0;

        navigate('/campaigns', { 
          state: { 
            message: `Campaign created! ${uploadedCount} contact${uploadedCount !== 1 ? 's' : ''} uploaded.${skippedCount > 0 ? ` (${skippedCount} skipped)` : ''}`
          } 
        });
      } else {
        // Check for usage plan limit exceeded error
        if (response.error?.code === 'ERR_USAGE_PLAN_LIMIT_EXCEEDED') {
          setUpgradeDialogType('usage_limit');
          setUpgradeDialogOpen(true);
          setIsUploading(false);
          return;
        }
        throw new Error(response.message || response.error?.message || 'Failed to upload contacts');
      }
    } catch (err: any) {
      // Check if error is from API response with ERR_USAGE_PLAN_LIMIT_EXCEEDED
      if (err?.code === 'ERR_USAGE_PLAN_LIMIT_EXCEEDED' || err?.error?.code === 'ERR_USAGE_PLAN_LIMIT_EXCEEDED' || err?.response?.data?.error?.code === 'ERR_USAGE_PLAN_LIMIT_EXCEEDED') {
        setUpgradeDialogType('usage_limit');
        setUpgradeDialogOpen(true);
        setIsUploading(false);
        return;
      }
      setError(err instanceof Error ? err.message : err?.error?.message || 'An error occurred');
      setIsUploading(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Template Loading/Error Alert - Hidden until next iteration */}
          {/* {isLoadingTemplates && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography>Loading templates...</Typography>
              </Box>
            </Alert>
          )}
          
          {templateError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTemplateError(null)}>
              {templateError}
            </Alert>
          )} */}
          
          {/* Page Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                color: '#111827',
                mb: 0.5
              }}
            >
              Upload Customer List
            </Typography>
            <Typography
            sx={{
                color: '#6B7280',
                fontSize: '0.875rem',
                fontFamily: '"Inter", sans-serif'
              }}
            >
              Upload your CSV file, preview contacts, and configure invitation settings
            </Typography>
          </Box>

          {/* Step 1: Upload CSV */}
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      fontSize: '1.125rem',
                      color: '#111827',
                      mb: 0.5
                    }}
                  >
                    Step 1: Upload CSV File
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.875rem',
                      color: '#6B7280'
                    }}
                  >
                    File should contain Name, Email, and Phone columns
                  </Typography>
                </Box>
                  <MuiLink
                    component="button"
                    onClick={handleDownloadTemplate}
                    sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#4F46E5',
                      fontSize: '0.875rem',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      '&:hover': {
                      color: '#4338CA'
                      }
                    }}
                  >
                  Download Template
                  </MuiLink>
                  </Box>

              {!currentFile ? (
                <Box
                  sx={{
                    border: '2px dashed #D1D5DB',
                    borderRadius: '8px',
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#F9FAFB',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#4F46E5',
                      bgcolor: '#EEF2FF'
                    }
                  }}
                  onClick={() => document.getElementById('csv-upload')?.click()}
                >
                  <CloudUpload sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.875rem',
                      color: '#111827',
                      fontWeight: 600,
                      mb: 0.5
                    }}
                  >
                    Click to upload CSV file
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.75rem',
                      color: '#6B7280'
                    }}
                  >
                    or drag and drop (Max 10MB)
                  </Typography>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={handleFileUpload}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    p: 2,
                    bgcolor: '#F9FAFB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle sx={{ color: '#10B981' }} />
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827'
                        }}
                      >
                        {currentFile.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '0.75rem',
                          color: '#6B7280'
                        }}
                      >
                        {totalRows} contacts • {(currentFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Delete />}
                    onClick={handleRemoveFile}
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      color: '#EF4444',
                      textTransform: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
            </CardContent>
          </StyledCard>

          {/* Step 2: Preview Data */}
          {csvPreview.length > 0 && (
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#111827',
                    mb: 2
                  }}
                >
                  Step 2: Preview Contacts (First 10 rows)
                </Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E5E7EB' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                        <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600, color: '#111827' }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600, color: '#111827' }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600, color: '#111827' }}>
                          Phone
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvPreview.map((row, index) => (
                        <TableRow key={index} hover>
                          <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>
                            {row.name || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>
                            {row.email || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>
                            {row.phone || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalRows > 10 && (
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      mt: 2,
                      textAlign: 'center'
                    }}
                  >
                    + {totalRows - 10} more contacts in the file
                  </Typography>
                  )}
                </CardContent>
              </StyledCard>
          )}

          {/* Step 3: Select Channels (Multi-Select) */}
          {csvPreview.length > 0 && (
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#111827',
                    mb: 1
                  }}
                >
                  Step 3: Select Communication Channels
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.875rem',
                    color: '#6B7280',
                      mb: 3
                    }}
                  >
                  You can select multiple channels to reach your customers
                  </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2
                  }}
                >
                  {/* Email - First */}
                  <Box
                    onClick={() => {
                      const newChannels = selectedChannels.includes('email')
                        ? selectedChannels.filter(c => c !== 'email')
                        : [...selectedChannels, 'email'];
                      if (newChannels.length > 0) setSelectedChannels(newChannels);
                    }}
                    sx={{
                      border: selectedChannels.includes('email') ? '2px solid #4F46E5' : '2px solid #E5E7EB',
                      borderRadius: '12px',
                      p: 3,
                      cursor: 'pointer',
                      bgcolor: selectedChannels.includes('email') ? '#EEF2FF' : 'white',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#4F46E5',
                        bgcolor: '#EEF2FF'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Email sx={{ fontSize: 32, color: selectedChannels.includes('email') ? '#4F46E5' : '#6B7280' }} />
                      {selectedChannels.includes('email') && (
                        <CheckCircle sx={{ color: '#4F46E5' }} />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#111827',
                        mb: 0.5
                      }}
                    >
                      Email
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.75rem',
                        color: '#6B7280'
                      }}
                    >
                      Send via email
                    </Typography>
                  </Box>

                  {/* SMS - Second (Disabled/Coming Soon) */}
                  <Box
                    sx={{
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      p: 3,
                      cursor: 'not-allowed',
                      bgcolor: '#F9FAFB',
                      opacity: 0.6,
                      position: 'relative'
                    }}
                  >
                    <Chip
                      label="Coming Soon"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: '#FEF3C7',
                        color: '#92400E',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        height: 20
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Sms sx={{ fontSize: 32, color: '#9CA3AF' }} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#9CA3AF',
                        mb: 0.5
                      }}
                    >
                      SMS
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.75rem',
                        color: '#9CA3AF'
                      }}
                    >
                      Send via text message
                    </Typography>
                  </Box>

                  {/* WhatsApp - Third (Disabled/Coming Soon) */}
                  <Box
                    sx={{
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      p: 3,
                      cursor: 'not-allowed',
                      bgcolor: '#F9FAFB',
                      opacity: 0.6,
                      position: 'relative'
                    }}
                  >
                    <Chip
                      label="Coming Soon"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: '#FEF3C7',
                        color: '#92400E',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        height: 20
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <WhatsApp sx={{ fontSize: 32, color: '#9CA3AF' }} />
            </Box>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: '#9CA3AF',
                        mb: 0.5
                      }}
                    >
                      WhatsApp
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.75rem',
                        color: '#9CA3AF'
                      }}
                    >
                      Send via WhatsApp
                    </Typography>
          </Box>
                </Box>
              </CardContent>
            </StyledCard>
          )}

          {/* Step 4: Select Template */}
          {csvPreview.length > 0 && selectedChannels.length > 0 && (
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography
            sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: '#111827',
                    mb: 2
                  }}
                >
                  Step 4: Choose Invitation Template
                </Typography>

                {/* SMS Template Accordion - Hidden since SMS is coming soon */}
                {false && selectedChannels.includes('sms') && (
                  <Accordion
                    expanded={expandedAccordion === 'sms'}
                    onChange={() => setExpandedAccordion(expandedAccordion === 'sms' ? false : 'sms')}
                    sx={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      mb: 2,
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Sms sx={{ color: '#4F46E5' }} />
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#111827'
                          }}
                        >
                          SMS Templates
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <RadioGroup
                        value={selectedSmsTemplate}
                        onChange={(e) => setSelectedSmsTemplate(e.target.value)}
                      >
                        {smsTemplates.map((template) => (
                          <Box
                            key={template.id}
              sx={{
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              p: 2,
                              mb: 2,
                              bgcolor: selectedSmsTemplate === template.id ? '#F9FAFB' : 'transparent'
                            }}
                          >
                            <FormControlLabel
                              value={template.id}
                              control={<Radio />}
                              label={
                                <Box>
              <Typography
                sx={{
                                      fontFamily: '"Inter", sans-serif',
                                      fontWeight: 600,
                  fontSize: '0.875rem',
                                      color: '#111827',
                                      mb: 0.5
                                    }}
                                  >
                                    {template.name}
                                  </Typography>
                      <Typography
                        sx={{
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '0.875rem',
                                      color: '#6B7280'
                        }}
                      >
                                    {template.content}
                      </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Or write your own custom SMS message..."
                        value={customSmsText}
                        onFocus={(e) => {
                          // Check if user is on Growth plan or higher
                          const userData = getFullUserData();
                          const usagePlan = userData.usagePlan?.toLowerCase() || 'starter plan';
                          const isGrowthOrHigher = usagePlan.includes('growth') || usagePlan.includes('pro') || usagePlan.includes('enterprise');
                          
                          if (!isGrowthOrHigher && !customSmsText) {
                            e.target.blur();
                            setUpgradeDialogType('template');
                            setUpgradeDialogOpen(true);
                            return;
                          }
                        }}
                        onChange={(e) => {
                          // Check if user is on Growth plan or higher
                          const userData = getFullUserData();
                          const usagePlan = userData.usagePlan?.toLowerCase() || 'starter plan';
                          const isGrowthOrHigher = usagePlan.includes('growth') || usagePlan.includes('pro') || usagePlan.includes('enterprise');
                          
                          if (!isGrowthOrHigher && !customSmsText) {
                            setUpgradeDialogType('template');
                            setUpgradeDialogOpen(true);
                            return;
                          }
                          setCustomSmsText(e.target.value);
                        }}
                        sx={{
                          mt: 2,
                          '& .MuiOutlinedInput-root': {
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* WhatsApp Template Accordion */}
                {selectedChannels.includes('whatsapp') && (
                  <Accordion
                    expanded={expandedAccordion === 'whatsapp'}
                    onChange={() => setExpandedAccordion(expandedAccordion === 'whatsapp' ? false : 'whatsapp')}
                    sx={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      mb: 2,
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WhatsApp sx={{ color: '#4F46E5' }} />
                      <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#111827'
                          }}
                        >
                          WhatsApp Templates
                      </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <RadioGroup
                        value={selectedWhatsappTemplate}
                        onChange={(e) => setSelectedWhatsappTemplate(e.target.value)}
                      >
                        {WHATSAPP_TEMPLATES.map((template) => (
                          <Box
                            key={template.id}
                            sx={{
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              p: 2,
                              mb: 2,
                              bgcolor: selectedWhatsappTemplate === template.id ? '#F9FAFB' : 'transparent'
                            }}
                          >
                            <FormControlLabel
                              value={template.id}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography
                                    sx={{
                                      fontFamily: '"Inter", sans-serif',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      color: '#111827',
                                      mb: 0.5
                                    }}
                                  >
                                    {template.name}
              </Typography>
                                  <Typography
                                    sx={{
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '0.875rem',
                                      color: '#6B7280',
                                      whiteSpace: 'pre-line'
                                    }}
                                  >
                                    {template.content}
                                  </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Or write your own custom WhatsApp message..."
                        value={customWhatsappText}
                        onChange={(e) => setCustomWhatsappText(e.target.value)}
                        sx={{
                          mt: 2,
                          '& .MuiOutlinedInput-root': {
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Email Template Accordion */}
                {selectedChannels.includes('email') && (
                  <Accordion
                    expanded={expandedAccordion === 'email'}
                    onChange={() => setExpandedAccordion(expandedAccordion === 'email' ? false : 'email')}
                    sx={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ color: '#4F46E5' }} />
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: '#111827'
                          }}
                        >
                          Email Templates
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <RadioGroup
                        value={selectedEmailTemplate}
                        onChange={(e) => setSelectedEmailTemplate(e.target.value)}
                      >
                        {emailTemplates.map((template) => (
                          <Box
                            key={template.id}
                sx={{
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              p: 2,
                              mb: 2,
                              bgcolor: selectedEmailTemplate === template.id ? '#F9FAFB' : 'transparent'
                            }}
                          >
                            <FormControlLabel
                              value={template.id}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography
                                    sx={{
                                      fontFamily: '"Inter", sans-serif',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      color: '#111827',
                                      mb: 0.5
                                    }}
                                  >
                                    {template.name}
                                  </Typography>
                                  <Chip
                                    label={`Subject: ${template.subject}`}
                                    size="small"
                                    sx={{ mb: 1, fontSize: '0.75rem' }}
                                  />
                                  <Typography
                                    sx={{
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '0.875rem',
                                      color: '#6B7280',
                                      whiteSpace: 'pre-line'
                                    }}
                                  >
                                    {template.content}
                                  </Typography>
                                </Box>
                              }
                              sx={{ width: '100%', m: 0 }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Or write your own custom email message..."
                        value={customEmailText}
                        onFocus={(e) => {
                          // Check if user is on Growth plan or higher
                          const userData = getFullUserData();
                          const usagePlan = userData.usagePlan?.toLowerCase() || 'starter plan';
                          const isGrowthOrHigher = usagePlan.includes('growth') || usagePlan.includes('pro') || usagePlan.includes('enterprise');
                          
                          if (!isGrowthOrHigher && !customEmailText) {
                            e.target.blur();
                            setUpgradeDialogType('template');
                            setUpgradeDialogOpen(true);
                            return;
                          }
                        }}
                        onChange={(e) => {
                          // Check if user is on Growth plan or higher
                          const userData = getFullUserData();
                          const usagePlan = userData.usagePlan?.toLowerCase() || 'starter plan';
                          const isGrowthOrHigher = usagePlan.includes('growth') || usagePlan.includes('pro') || usagePlan.includes('enterprise');
                          
                          if (!isGrowthOrHigher && !customEmailText) {
                            setUpgradeDialogType('template');
                            setUpgradeDialogOpen(true);
                            return;
                          }
                          setCustomEmailText(e.target.value);
                        }}
                        sx={{
                          mt: 2,
                          '& .MuiOutlinedInput-root': {
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </StyledCard>
          )}

          {/* Action Buttons */}
          {csvPreview.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                onClick={() => navigate('/campaigns')}
                  sx={{
                  fontFamily: '"Inter", sans-serif',
                  height: 44,
                  px: 4,
                  color: '#111827',
                    borderColor: '#E5E7EB',
                    textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                    '&:hover': {
                    borderColor: '#D1D5DB',
                    bgcolor: '#F9FAFB'
                    }
                  }}
                >
                Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendInvitations}
                  disabled={isUploading || !currentFile}
                  sx={{
                  fontFamily: '"Inter", sans-serif',
                  height: 44,
                  px: 4,
                  bgcolor: '#4F46E5',
                    textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  boxShadow: 'none',
                    '&:hover': {
                    bgcolor: '#4338CA',
                    boxShadow: 'none'
                    },
                    '&.Mui-disabled': {
                    bgcolor: '#E5E7EB',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  {isUploading ? (
                    <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Sending...
                    </>
                  ) : (
                  `Send Invitations to ${totalRows} Contacts`
                  )}
                </Button>
              </Box>
          )}
        </ContentArea>
      </MainContent>

      {/* Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Upgrade Dialog */}
      <Dialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#111827',
            pb: 1
          }}
        >
          Upgrade to Growth Plan
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '0.875rem',
              color: '#6B7280',
              mb: 2
            }}
          >
            {upgradeDialogType === 'usage_limit'
              ? 'You\'ve reached your contact limit on the Starter Plan. Upgrade to Growth Plan to add more contacts and unlock advanced features.'
              : 'Custom template customization is available in the Growth plan and above. Upgrade now to unlock this feature and customize your email and SMS templates.'}
          </DialogContentText>
          <Box
            sx={{
              bgcolor: '#F9FAFB',
              borderRadius: '8px',
              p: 2,
              mb: 2
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#111827',
                mb: 1
              }}
            >
              Growth Plan includes:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Typography
                component="li"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  mb: 0.5
                }}
              >
                Custom email and SMS templates
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  mb: 0.5
                }}
              >
                Up to 500 review requests/month
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  color: '#6B7280',
                  mb: 0.5
                }}
              >
                Advanced AI Survey and Response generator
              </Typography>
              <Typography
                component="li"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '0.875rem',
                  color: '#6B7280'
                }}
              >
                Priority support
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setUpgradeDialogOpen(false)}
            sx={{
              fontFamily: '"Inter", sans-serif',
              textTransform: 'none',
              color: '#6B7280',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setUpgradeDialogOpen(false);
              // Navigate to pricing/billing page or open upgrade flow
              window.open('/#pricing', '_blank');
            }}
            variant="contained"
            sx={{
              fontFamily: '"Inter", sans-serif',
              textTransform: 'none',
              bgcolor: '#4F46E5',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#4338CA'
              }
            }}
          >
            Upgrade to Growth Plan
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default CustomerUpload;
